import { createSelector, createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { formatISO } from 'date-fns';
import { v4 } from 'uuid';

import { mifidApi } from '@/api/mifid';
import { getWorkspaceMatchFulfilled } from '@/api/workspace';
import { mapToFlexiForward } from '@/domain/FxFlexiForward/FxFlexiForward.utils';
import { flexiRepriceFromBlotterReducer } from '@/store/blotter/workflows/cashRepriceQuote/extraReducers.ts';
import {
  cancelFlexiForwardRfsMatchPending,
  checkFlexiForwardByTradeCaptureMatchFulfilled,
  checkFlexiForwardByTradeCaptureMatchRejected,
  flexiForwardApi,
  flexiForwardRequestRfsMatchPending,
} from '@/store/flexi-forward/api';
import { createFlexiForwardQuote } from '@/store/flexi-forward/api/createFlexiForwardQuote.ts';
import type {
  ReceivedFlexiForwardQuoteReply,
  ReceivedHeartbeat,
} from '@/store/flexi-forward/api/requestFlexiForwardRfs.model.ts';
import { getFlexiForwardRfsId } from '@/store/flexi-forward/helpers/rfs.ts';
import { generateFlexiForwardTile } from '@/store/flexi-forward/helpers/tile.ts';
import { updateFlexiForwardFromTradeCapture } from '@/store/flexi-forward/helpers/trade-capture.ts';
import type {
  FlexiForwardById,
  FlexiForwardProperties,
  FlexiForwardRfs,
  FxFlexiForward,
  MarginType,
  PremiumWay,
  SolvingType,
} from '@/store/flexi-forward/state.ts';
import { reduceFlexiForwardOnFetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { reduceFWStateOnSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import { duplicateTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { IQuoteAbort, IQuoteTraderNotification, QuoteWithPrice } from '@/utils/rfs/type.ts';

const createFlexiForwardSelector = createSelector.withTypes<FlexiForwardById>();

export const flexiForwardSlice = createSlice({
  name: 'flexiForward',
  initialState: {} as FlexiForwardById,
  reducers: {
    flexiForwardClientChanged(
      state,
      action: PayloadAction<
        | { tileId: string; clientId: number | undefined; isTabClientOverridden: true }
        | { tileId: string; clientId: number; isTabClientOverridden: false }
      >,
    ) {
      const { tileId, clientId, isTabClientOverridden } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        if (flexiForward.clientId === clientId) {
          return;
        }

        if (isTabClientOverridden) {
          flexiForward.clientId = clientId;
        }
      });
    },

    notionalCurrencyChanged: (
      state,
      {
        payload: { tileId, notionalCurrencyIndex },
      }: PayloadAction<{ tileId: string; notionalCurrencyIndex: CurrencyIndex }>,
    ) => {
      withFlexiForward(tileId, state, (flexiForward) => {
        flexiForward.properties.notionalCurrencyIndex = notionalCurrencyIndex;
      });
    },

    solvingTypeChanged: (state, { payload }: PayloadAction<{ tileId: string; solvingType: SolvingType }>) => {
      const { tileId, solvingType } = payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        flexiForward.properties.solvingType = solvingType;

        if (solvingType !== 'solveRate') {
          flexiForward.properties.upfrontCurrency = flexiForward.properties.currency1;
        }
      });
    },

    premiumWayChanged: (state, { payload }: PayloadAction<{ tileId: string; premiumWay: PremiumWay }>) => {
      const { tileId, premiumWay } = payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        flexiForward.properties.premiumWay = premiumWay;
      });
    },

    marginTypeChanged: (state, { payload }: PayloadAction<{ tileId: string; marginType: MarginType }>) => {
      const { tileId, marginType } = payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        flexiForward.properties.marginType = marginType;
      });
    },

    setMaxDate: (state, action: PayloadAction<{ tileId: string; maxDate: string | undefined }>) => {
      const { tileId, maxDate } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        flexiForward.properties.maxDate = maxDate;
      });
    },

    optimisticUpdateHedgeAmount: (
      state,
      action: PayloadAction<{ tileId: string; deltaHedgeAmount: number | null }>,
    ) => {
      const { tileId, deltaHedgeAmount } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        flexiForward.properties.deltaHedgeAmount = deltaHedgeAmount;
      });
    },

    resetFlexiForward: (state, action: PayloadAction<{ tileId: string; clientId?: number }>) => {
      const { tileId, clientId } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        state[tileId] = generateFlexiForwardTile(flexiForward.properties.currencyPair, clientId);
      });
    },

    // RFS
    flexiForwardRfsQuoteReceived: (
      state,
      action: PayloadAction<{
        tileId: string;
        message: QuoteWithPrice<ReceivedFlexiForwardQuoteReply>;
      }>,
    ) => {
      const { tileId, message } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        if (isDefined(flexiForward.execution) || isNotDefined(flexiForward.properties.rfs)) {
          return;
        }

        const quote = createFlexiForwardQuote(tileId, message);

        if (flexiForward.properties.rfs.status === 'AWAITING' || flexiForward.properties.rfs.status === 'PRICING') {
          const initialRfsWindow =
            flexiForward.properties.rfs.status === 'PRICING'
              ? flexiForward.properties.rfs.initialRfsWindow
              : quote.rfsWindow;
          flexiForward.properties.rfs = {
            status: 'PRICING',
            rfsId: flexiForward.properties.rfs.rfsId,
            // requestedAt: flexiForward.properties.rfs.requestedAt,
            quoteType: quote.quoteType,
            quoteId: quote.quoteId,
            quote,
            initialRfsWindow,
          };
        }
      });
    },

    flexiForwardRfsTraderNotificationReceived: (
      state,
      action: PayloadAction<{ tileId: string; message: IQuoteTraderNotification<ReceivedFlexiForwardQuoteReply> }>,
    ) => {
      const { tileId, message } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        if (isNotDefined(flexiForward.properties.rfs)) {
          return;
        }

        const traderId = message.OptionQuoteReply.TraderId ?? undefined;

        if (flexiForward.properties.rfs.status === 'PRICING' || flexiForward.properties.rfs.status === 'AWAITING') {
          flexiForward.properties.rfs = {
            ...flexiForward.properties.rfs,
            traderId,
          };
        }
      });
    },

    flexiForwardRfsHeartbeatReceived: (
      _state,
      _action: PayloadAction<{ tileId: string; heartbeat: ReceivedHeartbeat }>,
    ) => {},

    flexiForwardRfsQuoteAborted: (state, action: PayloadAction<{ tileId: string; reply: IQuoteAbort }>) => {
      const { tileId, reply } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        const rfsState = flexiForward.properties.rfs;

        if (isNotDefined(rfsState)) {
          return;
        }

        if (reply.QuoteReject.ErrorCode === 'EM_TIMEOUT') {
          flexiForward.properties.rfs = makeExpired(rfsState);
        } else {
          flexiForward.properties.rfs = {
            status: 'ERROR',
            rfsId: getFlexiForwardRfsId(rfsState),
            error: {
              multipassErrorCode: reply.QuoteReject.MultipassErrorCode,
              errorCode: reply.QuoteReject.ErrorCode,
              errorMessage: reply.QuoteReject.ErrorMessage,
            },
          };
        }
      });
    },

    flexiForwardPending: (state, action: PayloadAction<{ tileId: string; executionId: string }>) => {
      const { tileId, executionId } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        flexiForward.execution = {
          status: 'PENDING',
          executionId,
        };

        if (isDefined(flexiForward.properties.rfs)) {
          flexiForward.properties.rfs = makeExpired(flexiForward.properties.rfs);
        }
      });
    },

    flexiForwardExecutionSucceed: (state, action: PayloadAction<{ tileId: string }>) => {
      withFlexiForward(action.payload.tileId, state, (flexiForward) => {
        if (isNotDefined(flexiForward.execution)) {
          return;
        }

        flexiForward.execution.status = 'SUCCESS';
      });
    },

    flexiForwardExecutionRejected: (state, action: PayloadAction<{ tileId: string }>) => {
      withFlexiForward(action.payload.tileId, state, (flexiForward) => {
        if (isNotDefined(flexiForward.execution)) {
          return;
        }

        flexiForward.execution.status = 'REJECTED';
      });
    },

    flexiForwardRejectedExecutionReset: (state, action: PayloadAction<{ tileId: string }>) => {
      withFlexiForward(action.payload.tileId, state, (flexiForward) => {
        if (isNotDefined(flexiForward.execution)) {
          return;
        }

        flexiForward.execution = undefined;
      });
    },

    flexiForwardExecutionReset: (state, action: PayloadAction<{ tileId: string }>) => {
      const { tileId } = action.payload;

      withFlexiForward(tileId, state, (flexiForward) => {
        flexiForward.execution = undefined;
      });
    },
  },

  extraReducers: (builder) => {
    reduceFWStateOnSharedTabRemoved(builder);

    builder.addCase(updateTileProductType, (state, { payload: { tileId, productType } }) => {
      // If tileId exist in state and productType !== FlexiForward so let's delete tile
      if (isDefined(state[tileId]) && productType !== 'FlexiForward') {
        delete state[tileId];
        return;
      }

      // If tileId doesn't exist and productType === FlexiForward, let's create new tile
      if (isNotDefined(state[tileId]) && productType === 'FlexiForward') {
        state[tileId] = generateFlexiForwardTile();
      }
    });

    builder.addCase(duplicateTile, (state, action) => {
      const { sourceTileId, newTileId } = action.payload;

      const sourceTile = state[sourceTileId];

      if (isNotDefined(sourceTile)) {
        return state;
      }

      if (isDefined(state[newTileId])) {
        return state;
      }

      const { rfs: _, ...tilePropertiesWithoutRfs } = sourceTile.properties;

      return {
        ...state,
        [newTileId]: {
          ...sourceTile,
          properties: tilePropertiesWithoutRfs,
          tradeCapture: {
            status: 'INITIALIZED',
            sessionId: v4(),
            version: -1,
          },
        },
      };
    });

    builder.addCase(removeTile, (state, { payload: tileId }) => {
      if (isNotDefined(state[tileId])) {
        return state;
      }

      delete state[tileId];
      return state;
    });

    flexiRepriceFromBlotterReducer(builder);

    // TRADE CAPTURE
    builder.addMatcher(
      flexiForwardApi.endpoints.checkFlexiForwardByTradeCapture.matchPending,
      function onTradeCaptureUpdated(state, action) {
        const { tileId } = action.meta.arg.originalArgs;
        withFlexiForward(tileId, state, (flexiForward) => {
          flexiForward.tradeCapture.status = 'CHECKING';
        });
      },
    );

    builder.addMatcher(checkFlexiForwardByTradeCaptureMatchFulfilled, (state, action): void => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentFlexiForward = state[tileId];
      const flexiForwardFromTradeCapture = action.payload;

      if (isNotDefined(currentFlexiForward) || isNotDefined(flexiForwardFromTradeCapture)) {
        return;
      }

      state[tileId] = updateFlexiForwardFromTradeCapture(currentFlexiForward, flexiForwardFromTradeCapture);
    });

    builder.addMatcher(checkFlexiForwardByTradeCaptureMatchRejected, (state, action): void => {
      const tileId = action.meta.arg.originalArgs.tileId;

      withFlexiForward(tileId, state, (flexiForward) => {
        const tradeCapture = flexiForward.tradeCapture;

        flexiForward.tradeCapture = {
          status: 'ERROR',
          sessionId: tradeCapture.sessionId,
          version: tradeCapture.version,
          error: 'TIMEOUT',
        };
      });
    });

    // RFS
    // REQUEST
    builder.addMatcher(flexiForwardRequestRfsMatchPending, function beforeRfsRequest(state, action) {
      const tileId = action.meta.arg.originalArgs.tileId;

      withFlexiForward(tileId, state, (flexiForward) => {
        const rfsId = action.meta.arg.originalArgs.rfsId;
        flexiForward.properties.rfs = {
          status: 'AWAITING',
          rfsId,
          quoteType: action.meta.arg.originalArgs.body.forceTradable ? 'Tradeable' : 'Indicative',
        };
      });
    });

    // CANCEL
    builder.addMatcher(cancelFlexiForwardRfsMatchPending, function beforeCancelRfs(state, action) {
      const { tileId, removeQuote } = action.meta.arg.originalArgs;

      withFlexiForward(tileId, state, (flexiForward) => {
        if (removeQuote) {
          flexiForward.properties.rfs = {
            status: 'CANCELLED',
          };
        } else if (isDefined(flexiForward.properties.rfs)) {
          flexiForward.properties.rfs = makeExpired(flexiForward.properties.rfs);
        }
      });
    });

    // TIMESTAMP
    builder.addMatcher(mifidApi.endpoints.recordPrice.matchFulfilled, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      withFlexiForward(tileId, state, (flexiForward) => {
        if (isNotDefined(flexiForward.properties.mifid)) {
          return;
        }

        flexiForward.properties.mifid.records.push({ date: formatISO(action.meta.arg.originalArgs.date) });
      });
    });
    builder.addMatcher(mifidApi.endpoints.recordPrice.matchRejected, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      withFlexiForward(tileId, state, (flexiForward) => {
        if (isNotDefined(flexiForward.properties.mifid)) {
          return;
        }

        flexiForward.properties.mifid.records.push({
          date: formatISO(action.meta.arg.originalArgs.date),
          isError: true,
        });
      });
    });

    // WORKSPACE
    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      // clear all products created before workspace has loaded
      Object.keys(state).forEach((id) => {
        delete state[id];
      });

      action.payload.products.flexiForward.forEach((flexiForwardProduct) => {
        state[flexiForwardProduct.tileId] = mapToFlexiForward(flexiForwardProduct);
      });
    });

    reduceFlexiForwardOnFetchSharedWorkspace(builder);
  },

  selectors: {
    getAllFlexiForwardProducts: (state) => Object.entries(state).map(([tileId, product]) => ({ tileId, ...product })),
    getFlexiForwardTile: (state, tileId: string): FxFlexiForward | undefined => state[tileId],
    getFlexiForwardTileProperties: createFlexiForwardSelector(
      [(state, tileId: string) => state[tileId]],
      (flexiForwardTile): FlexiForwardProperties | undefined => flexiForwardTile?.properties,
    ),
  },
});

export const {
  flexiForwardClientChanged,
  notionalCurrencyChanged,
  solvingTypeChanged,
  premiumWayChanged,
  optimisticUpdateHedgeAmount,
  marginTypeChanged,
  resetFlexiForward,
  flexiForwardRfsQuoteReceived,
  flexiForwardRfsQuoteAborted,
  flexiForwardPending,
  flexiForwardExecutionSucceed,
  flexiForwardExecutionRejected,
  flexiForwardRejectedExecutionReset,
  flexiForwardExecutionReset,
  flexiForwardRfsHeartbeatReceived,
  flexiForwardRfsTraderNotificationReceived,
} = flexiForwardSlice.actions;

export const { getAllFlexiForwardProducts } = flexiForwardSlice.selectors;

function withFlexiForward(
  tileId: string,
  state: FlexiForwardById,
  action: (flexiForward: Draft<FxFlexiForward>) => void,
) {
  const currentFlexiForward = state[tileId];

  if (isDefined(currentFlexiForward)) {
    action(currentFlexiForward);
  }
}

function makeExpired(rfs: FlexiForwardRfs): FlexiForwardRfs {
  if (rfs.status === 'EXPIRED' || rfs.status === 'PRICING') {
    return {
      status: 'EXPIRED',
      quote: rfs.quote,
    };
  }
  return { status: 'CANCELLED' };
}
